import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Footer from "./Footer";
import useSingleProduct from "../Hooks/useSingleProduct"; // Import your custom hook
import NavbarWithPushContent from "./NavbarWithPushContent";
import Navbar from "./Navbar";
import ErrorComponent from "../Assets/ErrorComponent";
import LoadingSpinner from "../Assets/LoadingSpinner";
import SimilarProducts from "./SimilarProducts";
import { useTranslation } from "react-i18next";
import NotFound from "../Assets/NotFound";

interface ProductDetailProps {
    products: any[]; // Replace `any` with your product type if needed
    lang: string;
}

const ProductDetail: React.FC<ProductDetailProps> = ({ products, lang }) => {
    const { t } = useTranslation();

    const { id } = useParams<{ id: string }>(); // Extract the `id` from the URL
    const { product, loading, error } = useSingleProduct(id || null); // If id is undefined, use null instead
    const [category, setCategory] = useState<string>("");

    useEffect(() => {
        if (!product?.category) return;

        switch (product.category) {
            case "681e0091c7f9a6cd6e6c8bba":
                setCategory("Medicinal%20Products/OTC");
                break;
            case "6806261f60c95e292c446d1c":
                setCategory("CBD%20Products/Oral");
                break;

            case "6806264860c95e292c446d24":
                setCategory("CBD%20Products/Topical");
                break;
            case "6806265f60c95e292c446d28":
                setCategory("CBD%20Products/Cosmetics");
                break;
            case "6809ec9860c95e292c454ba5":
                setCategory("Herbal%20Products");
                break;
            case "681b329160c95e292c4dbf7a":
                setCategory("Dietary%20Supplements/GALAHEALTH");
                break;
            case "681b333360c95e292c4dbfb9":
                setCategory("Dietary%20Supplements/JEKOGAL");
                break;
            case "681b337a60c95e292c4dbfc7":
                setCategory("Dietary%20Supplements/GALAPRO");
                break;
            case "6809ef4660c95e292c454c39":
                setCategory("BABYGAL%20Baby%20Skin Care");
                break;
            case "6809ef9660c95e292c454c52":
                setCategory("FACE%20&%20LIP%20CARE");
                break;
            case "6809efc360c95e292c454c62":
                setCategory("SENSILIS%20MAM%20CARE");
                break;
            case "6809efee60c95e292c454c69":
                setCategory("SENSILIS%20FOOT%20CARE");
                break;
            case "6809f01b60c95e292c454c73":
                setCategory("SENSILIS%20NAIL%20&%20HAND%20CARE");
                break;
            case "6809f04260c95e292c454c84":
                setCategory("SENSILIS%20OILS");
                break;
            case "6809f06a60c95e292c454c8e":
                setCategory("SILK%20HAIR%20REMOVAL");
                break;

            case "6809f25a60c95e292c45501e":
                setCategory("SOLARUS%20SUN%20PROTECTION%20CARE");
                break;
            case "6812212560c95e292c4916b5":
                setCategory("CITROGALA%20INSPECT%20PROTECTION");
                break;


            default:
                setCategory("OTC");
        }
    }, [product?.category]);


    return (
        <div className="min-h-screen flex flex-col bg-gray-50">
            <div className="hidden md:block">
                <NavbarWithPushContent products={products} lang={lang} />
            </div>

            <div className="block md:hidden">
                <Navbar products={products} lang={lang} />
            </div>

            <main className="flex-grow">
                <div className="max-w-6xl mx-auto px-4 pt-28 md:pt-32 pb-12">
                    {loading ? (
                        <LoadingSpinner />
                    ) : error ? (
                        <ErrorComponent message={error} />
                    ) : product ? (
                        <div className="overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-md">
                            <div className="grid items-center gap-8 p-6 sm:p-10 lg:grid-cols-2 lg:gap-12 lg:p-14">
                                {/* Product image with a soft sage halo */}
                                <div className="relative order-1 flex items-center justify-center lg:order-2">
                                    <div className="absolute h-56 w-56 rounded-full bg-brand-100/50 blur-3xl sm:h-72 sm:w-72" />
                                    <img
                                        src={product.image}
                                        alt={product.name?.[lang] || "Galafarm"}
                                        className="relative h-64 w-auto object-contain drop-shadow-2xl sm:h-80 lg:h-[26rem]"
                                    />
                                </div>

                                {/* Product info */}
                                <div className="order-2 text-center lg:order-1 lg:text-left">
                                    <span className="mx-auto mb-5 block h-1 w-12 rounded-full bg-brand-400 lg:mx-0" />
                                    <h1 className="mb-6 text-2xl font-bold leading-tight text-brand-gray sm:text-4xl font-montserrat">
                                        <span
                                            dangerouslySetInnerHTML={{
                                                __html: (product.name[lang] || "").replace(/®/g, '<sup style="font-size:20px;">®</sup>'),
                                            }}
                                        />
                                    </h1>

                                    <div
                                        className="prose mx-auto max-w-prose text-gray-600 lg:mx-0 [&_ul]:list-none [&_ul]:pl-0"
                                        dangerouslySetInnerHTML={{
                                            __html: product.description?.[lang] || "",
                                        }}
                                    />

                                    {product.dosage?.[lang] && (
                                        <div className="mt-8 inline-flex items-center gap-2 rounded-full border border-brand-100 bg-brand-50 px-4 py-2 text-sm">
                                            <svg
                                                className="h-5 w-5 text-brand-500"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth={2}
                                                viewBox="0 0 24 24"
                                            >
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M3 7l9-4 9 4-9 4-9-4zm0 5l9 4 9-4m-9 4v8" />
                                            </svg>
                                            <b className="text-brand-gray">{t("similarProduct.packaging")}</b>
                                            <span className="text-gray-500">{product.dosage?.[lang]}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <NotFound />
                    )}
                </div>

                {product && (
                    <SimilarProducts product={category} excludeProductId={product._id} lang={lang} />
                )}
            </main>

            <Footer />
        </div>
    );
};

export default ProductDetail;
